/* eslint-disable import/no-cycle */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-plusplus */
/* eslint-disable no-param-reassign */
/* eslint-disable radix */

import { FormItemT, PathMaps, SerializablePathMaps } from '../types';
import { isSectionSchema } from '../utils';

/**
 * Generate a structural signature from form schema
 * Only changes when fields/sections are added/removed/reordered
 * Does NOT change when field properties (label, text, etc.) are modified
 */
export const generateSchemaSignature = (formSchema: FormItemT[]): string => {
  const getStructure = (items: FormItemT[]): any[] => {
    return items.map((item) => {
      if (isSectionSchema(item)) {
        return {
          type: 'section',
          id: item.id,
          fields: getStructure(item.fields),
        };
      }
      return {
        type: 'field',
        id: item.id,
        hasFollowUp: !!item.followUpQuestionList?.length,
        followUps: item.followUpQuestionList?.length ? getStructure(item.followUpQuestionList) : [],
      };
    });
  };

  const structure = getStructure(formSchema);
  return JSON.stringify(structure);
};

/**
 * Builds path maps from form schema
 */
export const buildPathMaps = (formSchema: FormItemT[]): PathMaps => {
  const sectionToFirstFieldMap = new Map<string, number>();
  const fieldIdToIndexMap = new Map<string, number>();
  const indexToFieldIdMap = new Map<number, string>();

  if (!formSchema || formSchema.length === 0) {
    return { sectionToFirstFieldMap, fieldIdToIndexMap, indexToFieldIdMap };
  }

  const flattenToPayload = (items: FormItemT[], payloadIndex: { current: number }): void => {
    for (const item of items) {
      if (isSectionSchema(item)) {
        const firstFieldIndex = payloadIndex.current;
        sectionToFirstFieldMap.set(item.id, firstFieldIndex);
        flattenToPayload(item.fields, payloadIndex);
      } else {
        const currentIndex = payloadIndex.current;

        fieldIdToIndexMap.set(item.id, currentIndex);
        indexToFieldIdMap.set(currentIndex, item.id);

        if (item.section && !sectionToFirstFieldMap.has(item.section)) {
          sectionToFirstFieldMap.set(item.section, currentIndex);
        }

        payloadIndex.current++;

        if (item.followUpQuestionList?.length) {
          flattenToPayload(item.followUpQuestionList, payloadIndex);
        }
      }
    }
  };

  flattenToPayload(formSchema, { current: 0 });

  return { sectionToFirstFieldMap, fieldIdToIndexMap, indexToFieldIdMap };
};

/**
 * Convert Maps to serializable objects for Easy Peasy store
 */
export const serializePathMaps = (pathMaps: PathMaps, schemaSignature: string): SerializablePathMaps => {
  return {
    sectionToFirstField: Object.fromEntries(pathMaps.sectionToFirstFieldMap),
    fieldIdToIndex: Object.fromEntries(pathMaps.fieldIdToIndexMap),
    indexToFieldId: Object.fromEntries(Array.from(pathMaps.indexToFieldIdMap.entries()).map(([k, v]) => [k.toString(), v])),
    schemaSignature,
  };
};

/**
 * Convert serializable objects back to Maps
 */
export const deserializePathMaps = (serialized: SerializablePathMaps): PathMaps => {
  return {
    sectionToFirstFieldMap: new Map(Object.entries(serialized.sectionToFirstField)),
    fieldIdToIndexMap: new Map(Object.entries(serialized.fieldIdToIndex)),
    indexToFieldIdMap: new Map(Object.entries(serialized.indexToFieldId).map(([k, v]) => [parseInt(k), v])),
  };
};

/**
 * Check if schema structure has changed (needs rebuild)
 */
export const hasSchemaStructureChanged = (formSchema: FormItemT[], currentSignature: string | null): boolean => {
  if (!currentSignature) return true;
  const newSignature = generateSchemaSignature(formSchema);
  return newSignature !== currentSignature;
};

export const isStructuralUpdate = (updatedItem: Partial<FormItemT>): boolean => {
  // Check if any structural properties are being updated
  const structuralProps = ['id', 'order', 'followUpQuestionList', 'fields'];
  return Object.keys(updatedItem).some((key) => structuralProps.includes(key));
};
